import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { 
  ArrowLeft, 
  Sun, 
  Flower2,
  Waves,
  BookOpen,
  ChevronRight,
  ChevronLeft,
  Heart,
  Sparkles,
  Lock,
  Check,
  PenLine,
  Save,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

// Données des 3 parcours
const PARCOURS = {
  '21marches': {
    id: '21marches',
    title: 'Les 21 Marches',
    subtitle: 'de la Lumière Intérieure',
    description: 'Un chemin spirituel en 21 étapes vers votre lumière intérieure',
    icon: Sun,
    color: 'amber',
    gradient: 'from-amber-500 to-orange-500',
    chapters: [
      { num: 1, title: 'Le Silence', subtitle: 'La Porte de l\'être' },
      { num: 2, title: 'Le Souffle', subtitle: 'L\'Ancre de l\'Instant' },
      { num: 3, title: 'La Gratitude', subtitle: 'La Clé du Cœur' },
      { num: 4, title: 'Le Pardon', subtitle: 'La Libération' },
      { num: 5, title: 'L\'Amour', subtitle: 'La Flamme Universelle' },
      { num: 6, title: 'La Confiance', subtitle: 'Le Pas dans l\'Inconnu' },
      { num: 7, title: 'La Joie', subtitle: 'L\'Éclat de l\'âme' },
      { num: 8, title: 'La Patience', subtitle: 'Le Temps du Cœur' },
      { num: 9, title: 'La Compassion', subtitle: 'Le Regard du Cœur' },
      { num: 10, title: 'La Simplicité', subtitle: 'Le Retour à l\'Essentiel' },
      { num: 11, title: 'La Présence', subtitle: 'L\'Ici et Maintenant' },
      { num: 12, title: 'L\'Écoute', subtitle: 'La Voix du Silence' },
      { num: 13, title: 'La Vérité', subtitle: 'La Transparence de l\'être' },
      { num: 14, title: 'L\'Humilité', subtitle: 'La Force du Vide' },
      { num: 15, title: 'La Persévérance', subtitle: 'Le Pas qui Continue' },
      { num: 16, title: 'Le Courage', subtitle: 'La Force du Cœur' },
      { num: 17, title: 'La Liberté', subtitle: 'L\'Envol de l\'âme' },
      { num: 18, title: 'La Lumière', subtitle: 'Le Feu Intérieur' },
      { num: 19, title: 'L\'Unité', subtitle: 'Le Tout en Moi' },
      { num: 20, title: 'La Paix', subtitle: 'L\'Océan Intérieur' },
      { num: 21, title: 'Retour à la Maison', subtitle: 'La Lumière Retrouvée' }
    ]
  },
  'independances': {
    id: 'independances',
    title: 'In-Dépendances',
    subtitle: 'Se libérer pour s\'épanouir',
    description: 'Un parcours de 21 étapes pour surmonter ses dépendances',
    icon: Flower2,
    color: 'purple',
    gradient: 'from-purple-500 to-pink-500',
    chapters: [
      { num: 1, title: 'Reconnaître', subtitle: 'Ma dépendance' },
      { num: 2, title: 'Accepter', subtitle: 'Sans se juger' },
      { num: 3, title: 'Respirer', subtitle: 'Pour apaiser le mental' },
      { num: 4, title: 'Identifier', subtitle: 'Mes déclencheurs' },
      { num: 5, title: 'Créer', subtitle: 'Une réponse alternative' },
      { num: 6, title: 'Demander', subtitle: 'De l\'aide' },
      { num: 7, title: 'Nourrir', subtitle: 'Mon corps et mon esprit' },
      { num: 8, title: 'Transformer', subtitle: 'Mes pensées' },
      { num: 9, title: 'Trouver', subtitle: 'Un sens plus grand' },
      { num: 10, title: 'Célébrer', subtitle: 'Mes victoires' },
      { num: 11, title: 'Pardonner', subtitle: 'Mes chutes' },
      { num: 12, title: 'Construire', subtitle: 'Ma nouvelle identité' },
      { num: 13, title: 'Créer', subtitle: 'De nouvelles habitudes' },
      { num: 14, title: 'Bâtir', subtitle: 'Ma communauté de soutien' },
      { num: 15, title: 'Apprendre', subtitle: 'À dire non' },
      { num: 16, title: 'Cultiver', subtitle: 'La gratitude' },
      { num: 17, title: 'Développer', subtitle: 'Ma discipline douce' },
      { num: 18, title: 'Nourrir', subtitle: 'Ma créativité' },
      { num: 19, title: 'Reconnecter', subtitle: 'Avec la nature' },
      { num: 20, title: 'Servir', subtitle: 'Les autres' },
      { num: 21, title: 'Vivre', subtitle: 'Ma mission' }
    ]
  },
  'passages': {
    id: 'passages',
    title: 'Passages',
    subtitle: 'Traverser les transitions',
    description: 'Un guide de 21 passages pour naviguer les transitions de vie',
    icon: Waves,
    color: 'cyan',
    gradient: 'from-cyan-500 to-blue-500',
    chapters: [
      { num: 1, title: 'Le passage et l\'aube', subtitle: 'Nouveau départ' },
      { num: 2, title: 'Le passage et la peur', subtitle: 'Face à l\'inconnu' },
      { num: 3, title: 'Le passage et le silence', subtitle: 'Écouter l\'intérieur' },
      { num: 4, title: 'Le passage et la nuit', subtitle: 'Traverser l\'obscurité' },
      { num: 5, title: 'Le passage et le souffle', subtitle: 'Respirer la vie' },
      { num: 6, title: 'Le passage et le feu', subtitle: 'Transformation' },
      { num: 7, title: 'Le passage et l\'eau', subtitle: 'Fluidité' },
      { num: 8, title: 'Le passage et la mémoire', subtitle: 'Honorer le passé' },
      { num: 9, title: 'Le passage et l\'épreuve', subtitle: 'Force intérieure' },
      { num: 10, title: 'Le passage et la lumière', subtitle: 'Illumination' },
      { num: 11, title: 'Le passage et la solitude', subtitle: 'Être avec soi' },
      { num: 12, title: 'Le passage et l\'attente', subtitle: 'Patience sacrée' },
      { num: 13, title: 'Le passage et la rencontre', subtitle: 'L\'autre comme miroir' },
      { num: 14, title: 'Le passage et le doute', subtitle: 'Questionnement' },
      { num: 15, title: 'Le passage et la confiance', subtitle: 'Lâcher prise' },
      { num: 16, title: 'Le passage et la blessure', subtitle: 'Accepter la douleur' },
      { num: 17, title: 'Le passage et la guérison', subtitle: 'Renaissance' },
      { num: 18, title: 'Le passage et l\'abandon', subtitle: 'S\'en remettre' },
      { num: 19, title: 'Le passage et la joie', subtitle: 'Célébration' },
      { num: 20, title: 'Le passage et la paix', subtitle: 'Sérénité' },
      { num: 21, title: 'Le passage et l\'unité', subtitle: 'Complétude' }
    ]
  }
};

// Composant principal du Module Méditation
const MeditationModule = ({ user }) => {
  const navigate = useNavigate();
  const [selectedParcours, setSelectedParcours] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [progress, setProgress] = useState({});
  const [journalEntries, setJournalEntries] = useState({});
  const [showJournal, setShowJournal] = useState(false);
  const [currentJournalText, setCurrentJournalText] = useState('');

  // Charger la progression depuis localStorage
  useEffect(() => {
    const savedProgress = localStorage.getItem('meditation_progress');
    const savedJournal = localStorage.getItem('meditation_journal');
    if (savedProgress) setProgress(JSON.parse(savedProgress));
    if (savedJournal) setJournalEntries(JSON.parse(savedJournal));
  }, []);

  // Sauvegarder la progression
  const saveProgress = (parcoursId, chapterNum) => {
    const key = `${parcoursId}_${chapterNum}`;
    const newProgress = { ...progress, [key]: true };
    setProgress(newProgress);
    localStorage.setItem('meditation_progress', JSON.stringify(newProgress));
    toast.success('Progression sauvegardée ✨');
  };

  // Sauvegarder une entrée de journal
  const saveJournalEntry = () => {
    if (!currentJournalText.trim()) return;
    
    const key = `${selectedParcours.id}_${selectedChapter.num}`;
    const newEntries = { 
      ...journalEntries, 
      [key]: {
        text: currentJournalText,
        date: new Date().toISOString()
      }
    };
    setJournalEntries(newEntries);
    localStorage.setItem('meditation_journal', JSON.stringify(newEntries));
    toast.success('Journal sauvegardé 📝');
  };

  // Calculer le pourcentage de progression d'un parcours
  const getParcoursProgress = (parcoursId) => {
    const parcours = PARCOURS[parcoursId];
    if (!parcours) return 0;
    const completed = parcours.chapters.filter(ch => 
      progress[`${parcoursId}_${ch.num}`]
    ).length;
    return Math.round((completed / parcours.chapters.length) * 100);
  };

  // Vue d'accueil - Sélection des parcours
  if (!selectedParcours) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
        {/* Header */}
        <div className="bg-slate-900/80 border-b border-indigo-500/30 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')}
                className="text-indigo-300 hover:text-indigo-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <div className="text-center">
                <h1 className="text-2xl font-bold text-white flex items-center justify-center">
                  <Sparkles className="w-6 h-6 mr-2 text-indigo-400" />
                  Parcours Méditation
                </h1>
                <p className="text-sm text-indigo-300">Par Tincel E.</p>
              </div>
              <div className="w-20"></div>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <p className="text-lg text-indigo-200 leading-relaxed">
              Bienvenue dans cet espace de transformation intérieure. 
              Trois parcours de 21 étapes vous attendent, chacun offrant 
              un chemin unique vers la paix et la connaissance de soi.
            </p>
          </motion.div>

          {/* Grille des parcours */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.values(PARCOURS).map((parcours, index) => {
              const Icon = parcours.icon;
              const progressPct = getParcoursProgress(parcours.id);
              
              return (
                <motion.div
                  key={parcours.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.15 }}
                  whileHover={{ scale: 1.03, y: -5 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card 
                    className={`cursor-pointer bg-slate-800/50 border-${parcours.color}-500/30 hover:border-${parcours.color}-400/60 transition-all overflow-hidden`}
                    onClick={() => setSelectedParcours(parcours)}
                  >
                    <div className={`h-2 bg-gradient-to-r ${parcours.gradient}`} />
                    <CardContent className="p-6">
                      <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${parcours.gradient} flex items-center justify-center mx-auto mb-4`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white text-center mb-1">
                        {parcours.title}
                      </h3>
                      <p className={`text-sm text-${parcours.color}-300 text-center mb-3`}>
                        {parcours.subtitle}
                      </p>
                      <p className="text-sm text-slate-400 text-center mb-4">
                        {parcours.description}
                      </p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-slate-400">
                          <span>Progression</span>
                          <span>{progressPct}%</span>
                        </div>
                        <Progress value={progressPct} className="h-2" />
                      </div>
                      <Badge className={`mt-4 w-full justify-center bg-${parcours.color}-500/20 text-${parcours.color}-300 border-${parcours.color}-500/30`}>
                        21 étapes
                      </Badge>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Note */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center text-slate-500 text-sm mt-8"
          >
            💫 Votre progression est sauvegardée localement
          </motion.p>
        </div>
      </div>
    );
  }

  // Vue des chapitres d'un parcours
  if (!selectedChapter) {
    const Icon = selectedParcours.icon;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
        {/* Header */}
        <div className={`bg-gradient-to-r ${selectedParcours.gradient} py-8`}>
          <div className="max-w-4xl mx-auto px-4">
            <Button 
              variant="ghost" 
              onClick={() => setSelectedParcours(null)}
              className="text-white/80 hover:text-white mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tous les parcours
            </Button>
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">{selectedParcours.title}</h1>
                <p className="text-white/80">{selectedParcours.subtitle}</p>
              </div>
            </div>
            <div className="mt-4">
              <Progress value={getParcoursProgress(selectedParcours.id)} className="h-2 bg-white/20" />
              <p className="text-white/60 text-sm mt-1">
                {getParcoursProgress(selectedParcours.id)}% complété
              </p>
            </div>
          </div>
        </div>

        {/* Liste des chapitres */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedParcours.chapters.map((chapter, index) => {
              const isCompleted = progress[`${selectedParcours.id}_${chapter.num}`];
              
              return (
                <motion.div
                  key={chapter.num}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.03 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card 
                    className={`cursor-pointer transition-all ${
                      isCompleted 
                        ? `bg-${selectedParcours.color}-500/20 border-${selectedParcours.color}-500/50` 
                        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                    }`}
                    onClick={() => {
                      setSelectedChapter(chapter);
                      const journalKey = `${selectedParcours.id}_${chapter.num}`;
                      setCurrentJournalText(journalEntries[journalKey]?.text || '');
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                          isCompleted 
                            ? `bg-${selectedParcours.color}-500 text-white` 
                            : 'bg-slate-700 text-slate-300'
                        }`}>
                          {isCompleted ? <Check className="w-5 h-5" /> : chapter.num}
                        </div>
                        <ChevronRight className={`w-5 h-5 ${isCompleted ? `text-${selectedParcours.color}-400` : 'text-slate-500'}`} />
                      </div>
                      <h3 className={`font-semibold mt-3 ${isCompleted ? 'text-white' : 'text-slate-300'}`}>
                        {chapter.title}
                      </h3>
                      <p className={`text-sm ${isCompleted ? `text-${selectedParcours.color}-300` : 'text-slate-500'}`}>
                        {chapter.subtitle}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Vue d'un chapitre
  const Icon = selectedParcours.icon;
  const isCompleted = progress[`${selectedParcours.id}_${selectedChapter.num}`];
  const journalKey = `${selectedParcours.id}_${selectedChapter.num}`;
  const hasJournalEntry = journalEntries[journalKey];

  // Navigation entre chapitres
  const currentIndex = selectedParcours.chapters.findIndex(ch => ch.num === selectedChapter.num);
  const prevChapter = currentIndex > 0 ? selectedParcours.chapters[currentIndex - 1] : null;
  const nextChapter = currentIndex < selectedParcours.chapters.length - 1 ? selectedParcours.chapters[currentIndex + 1] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
      {/* Header compact */}
      <div className={`bg-gradient-to-r ${selectedParcours.gradient} py-4`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => setSelectedChapter(null)}
              className="text-white/80 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div className="text-center">
              <p className="text-white/60 text-sm">{selectedParcours.title}</p>
              <p className="text-white font-semibold">Étape {selectedChapter.num}/21</p>
            </div>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Contenu du chapitre */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Titre du chapitre */}
          <div className="text-center">
            <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${selectedParcours.gradient} flex items-center justify-center mx-auto mb-4`}>
              <span className="text-3xl font-bold text-white">{selectedChapter.num}</span>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">{selectedChapter.title}</h1>
            <p className={`text-xl text-${selectedParcours.color}-300`}>{selectedChapter.subtitle}</p>
          </div>

          {/* Enseignement */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <BookOpen className="w-5 h-5 mr-2 text-amber-400" />
                Enseignement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-300 leading-relaxed">
                {selectedChapter.title} représente une étape essentielle de votre parcours. 
                Prenez le temps de vous immerger dans cette réflexion et laissez les mots 
                résonner en vous.
              </p>
              <p className="text-slate-400 mt-4 italic">
                "Chaque pas sur ce chemin est une victoire. Chaque moment de conscience 
                est un cadeau que vous vous offrez."
              </p>
            </CardContent>
          </Card>

          {/* Méditation */}
          <Card className={`bg-${selectedParcours.color}-500/10 border-${selectedParcours.color}-500/30`}>
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Heart className={`w-5 h-5 mr-2 text-${selectedParcours.color}-400`} />
                Méditation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-slate-300">
                  Installez-vous confortablement. Fermez les yeux. Respirez profondément trois fois.
                </p>
                <div className={`p-4 rounded-lg bg-${selectedParcours.color}-500/20 border border-${selectedParcours.color}-500/30`}>
                  <p className={`text-${selectedParcours.color}-200 text-center text-lg font-medium`}>
                    "{selectedChapter.title} m'habite. Je suis en paix."
                  </p>
                </div>
                <p className="text-slate-400 text-sm text-center">
                  Répétez cette affirmation trois fois, lentement, en conscience.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Questions de réflexion */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Sparkles className="w-5 h-5 mr-2 text-purple-400" />
                Questions de réflexion
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start">
                  <span className="text-purple-400 mr-2">•</span>
                  Comment {selectedChapter.title.toLowerCase()} se manifeste dans votre vie quotidienne ?
                </li>
                <li className="flex items-start">
                  <span className="text-purple-400 mr-2">•</span>
                  Quel obstacle pourrait vous empêcher d'accéder à cet état ?
                </li>
                <li className="flex items-start">
                  <span className="text-purple-400 mr-2">•</span>
                  Quelle petite action pourriez-vous entreprendre aujourd'hui ?
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Journal personnel */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center">
                  <PenLine className="w-5 h-5 mr-2 text-emerald-400" />
                  Mon Journal
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowJournal(!showJournal)}
                  className="text-slate-400"
                >
                  {showJournal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </CardTitle>
              <CardDescription className="text-slate-400">
                Vos réflexions personnelles (privées et sauvegardées localement)
              </CardDescription>
            </CardHeader>
            {showJournal && (
              <CardContent>
                <Textarea
                  value={currentJournalText}
                  onChange={(e) => setCurrentJournalText(e.target.value)}
                  placeholder="Écrivez vos pensées, vos ressentis, vos prises de conscience..."
                  className="min-h-[150px] bg-slate-900/50 border-slate-600 text-slate-200"
                />
                <div className="flex justify-between items-center mt-4">
                  {hasJournalEntry && (
                    <p className="text-xs text-slate-500">
                      Dernière modification: {new Date(hasJournalEntry.date).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                  <Button 
                    onClick={saveJournalEntry}
                    className="bg-emerald-600 hover:bg-emerald-500"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Sauvegarder
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!isCompleted ? (
              <Button
                onClick={() => saveProgress(selectedParcours.id, selectedChapter.num)}
                className={`bg-gradient-to-r ${selectedParcours.gradient} text-white px-8`}
              >
                <Check className="w-4 h-4 mr-2" />
                Marquer comme complété
              </Button>
            ) : (
              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 px-6 py-2">
                <Check className="w-4 h-4 mr-2" />
                Étape complétée
              </Badge>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between pt-8 border-t border-slate-700">
            {prevChapter ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedChapter(prevChapter);
                  setCurrentJournalText(journalEntries[`${selectedParcours.id}_${prevChapter.num}`]?.text || '');
                }}
                className="border-slate-600 text-slate-300"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                {prevChapter.title}
              </Button>
            ) : <div />}
            
            {nextChapter ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedChapter(nextChapter);
                  setCurrentJournalText(journalEntries[`${selectedParcours.id}_${nextChapter.num}`]?.text || '');
                }}
                className="border-slate-600 text-slate-300"
              >
                {nextChapter.title}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => setSelectedChapter(null)}
                className={`bg-gradient-to-r ${selectedParcours.gradient}`}
              >
                Terminer le parcours
                <Sparkles className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MeditationModule;
